<script lang="ts">
  import { page } from '$app/stores';
  import { cx } from '@emotion/css';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { isHrefExternal } from '$lib/utils/links';
  import { TwistStack, ResponsiveVars } from '@twist/core';
  import DropdownTrigger from '$components/Dropdown/DropdownTrigger.svelte';
  import DropdownMenu from '$components/Dropdown/DropdownMenu.svelte';
  import Checkmark from '$components/Icons/Checkmark.svelte';
  import type { NavItem } from '$lib/sanity/schemas';
  import { MiscTypes } from '$lib/sanity/types';
  import { resolveHref, getDropdownItemsByParent } from './GlobalNav.svelte';
  import ChevronDown from '$components/Icons/ChevronDown.svelte';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';

  export let navItems: NavItem[] = [];
  export let activeItem: NavItem | undefined = undefined;
  export let handleDropdown: (item: NavItem) => void;
  export let handleCloseClicked: () => void;

  const dropdownItemsByParent = getDropdownItemsByParent(navItems);

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);

  const responsiveVars = new ResponsiveVars()
    .addFluid('nav-item-x', toFluidVarInput(8, 9))
    .addFluid('navigation-mobile-px', toFluidVarInput(12, 0))
    .addFluid('navigation-mobile-py', toFluidVarInput(21, 0))
    .addFluid('mobile-nav-item-py', toFluidVarInput(10, 10))
    .addFluid('mobile-nav-item-py-reskin', toFluidVarInput(4.5, 0))
    .addFluid('divider-my', toFluidVarInput(4.5, 0))
    .toCSS();
</script>

<div class={cx('navigation-mobile', responsiveVars, { reskin: brandReskinEnabled })}>
  <ul>
    {#each navItems as item, i}
      {#if item.itemType === 'folder' && item.folder}
        <span class="nav-item--mobile-root">
          <li>
            <DropdownTrigger
              class="nav-item"
              name={item.folder?.name}
              expanded={activeItem?.id === item.id}
              on:click={() => handleDropdown(item)}
            >
              <span class="nav-item__text" data-text={item.folder?.name}>
                {item.folder?.name}
              </span>
              <span class="dropdown-icon" class:dropdown-icon--active={activeItem?.id === item.id}>
                <ChevronDown />
              </span>
            </DropdownTrigger>
          </li>
        </span>
        {#if !brandReskinEnabled}
          <div class="nav-item-divider" />
        {/if}
        <DropdownMenu label={item.folder?.name} expanded={activeItem?.id === item.id}>
          <TwistStack direction="column" align="normal" justify="flex-start">
            {#each dropdownItemsByParent[item.id] as ddItem, i}
              {#if ddItem.link}
                <li
                  class="dropdown-item-wrapper"
                  class:last={i === dropdownItemsByParent[item.id].length - 1}
                >
                  <a
                    class="nav-item"
                    class:current={resolveHref(ddItem) === $page.url.pathname}
                    target={ddItem.link.destination.href &&
                    isHrefExternal(ddItem.link.destination.href)
                      ? '_blank'
                      : undefined}
                    href={resolveHref(ddItem)}
                    on:click={handleCloseClicked}
                    data-sveltekit-reload={ddItem.link.destination.page?._type ===
                      MiscTypes.Document}
                    aria-hidden={activeItem?.id === item.id}
                    tabindex={activeItem?.id === item.id ? 0 : -1}
                  >
                    {ddItem.link?.title}
                    {#if resolveHref(ddItem) === $page.url.pathname}
                      <span class="checkmark">
                        <Checkmark />
                      </span>
                    {/if}
                  </a>
                </li>
              {/if}
            {/each}
          </TwistStack>
        </DropdownMenu>
        {#if brandReskinEnabled}
          <div class="nav-item-divider" />
        {/if}
      {:else if item.itemType === 'link' && !item.parent && item.link}
        <span class="nav-item--mobile-root">
          <a
            class="nav-item"
            target={item.link.destination.href && isHrefExternal(item.link.destination.href)
              ? '_blank'
              : undefined}
            data-sveltekit-reload={item.link.destination.page?._type === MiscTypes.Document}
            href={resolveHref(item)}
            on:click={handleCloseClicked}
          >
            <span class="nav-item__text" data-text={item.link.title}>
              {item.link.title}
            </span>
          </a>
        </span>
        {#if i < navItems.length - 1 && !item.parent}
          <div class="nav-item-divider" />
        {/if}
      {/if}
    {/each}
  </ul>
</div>

<style lang="scss">
  @import 'src/scss/variables';
  @import 'src/scss/media';

  .navigation-mobile {
    display: flex;
    flex-direction: column;
    border-top: 1px solid rgba(0, 0, 0, 0.3);
    padding: var(--navigation-mobile-py) var(--navigation-mobile-px);
    overflow-y: scroll;

    @include at(lg) {
      display: none;
    }

    .nav-item-divider {
      margin: 0 var(--nav-item-x);
      border-top: 1px solid rgba(0, 0, 0, 0.3);
    }
  }

  .navigation-mobile.reskin {
    border-top: none;

    .nav-item-divider {
      margin: var(--divider-my) 0;
    }
  }
</style>
